import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Users2, CalendarClock, Wallet, ChevronRight } from "lucide-react";

const STATUS_OPTS = [
  { value: "", label: "—" },
  { value: "present", label: "Geldi", color: "bg-emerald-50 text-emerald-800 border-emerald-500" },
  { value: "absent", label: "Gelmedi", color: "bg-rose-50 text-rose-700 border-rose-500" },
  { value: "half", label: "Yarım gün", color: "bg-amber-50 text-amber-800 border-amber-500" },
  { value: "holiday", label: "Tatil", color: "bg-neutral-100 text-neutral-700 border-neutral-400" },
  { value: "leave", label: "İzin", color: "bg-indigo-50 text-indigo-700 border-indigo-500" },
];

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }

// HH:MM - HH:MM'den otomatik saat farkı → 8 saati aşarsa mesai (bilgi amaçlı, otomatik doldurmuyoruz)
function calcOvertime(check_in, check_out) {
  if (!check_in || !check_out) return null;
  const [h1, m1] = check_in.split(":").map(Number);
  const [h2, m2] = check_out.split(":").map(Number);
  if ([h1, m1, h2, m2].some((v) => isNaN(v))) return null;
  const min = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (min <= 0) return null;
  const hours = min / 60;
  return Math.max(0, +(hours - 8).toFixed(2));
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("timesheet");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    api.get("/employees").then(({ data }) => {
      setEmp((data || []).find((x) => x.id === id) || null);
    });
  }, [id]);

  if (!emp) return <div className="text-neutral-500 text-sm">Yükleniyor…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <Link to="/personel" className="hover:underline flex items-center gap-1"><ChevronLeft size={12} />Personel</Link>
        <span>/</span>
        <span className="text-neutral-700">{emp.name}</span>
      </div>
      <header className="brutal-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-black flex items-center justify-center bg-neutral-100">
            <Users2 size={22} />
          </div>
          <div>
            <div className="text-xs label-caps">{emp.position || "Personel"}</div>
            <h1 className="font-display text-2xl font-bold" data-testid="emp-detail-name">{emp.name}</h1>
            <div className="text-xs text-neutral-500 mt-0.5 space-x-3">
              {emp.phone && <span className="font-mono-num">{emp.phone}</span>}
              {emp.start_date && <span>İşe Başlama: <b>{fmtDate(emp.start_date)}</b></span>}
              {emp.monthly_salary && <span>Maaş: <b>{fmtTRY(emp.monthly_salary)}</b></span>}
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-black/10">
        <TabBtn active={tab === "timesheet"} onClick={() => setTab("timesheet")}
                icon={<CalendarClock size={14} />} label="Puantaj" tid="tab-timesheet" />
        <TabBtn active={tab === "advances"} onClick={() => setTab("advances")}
                icon={<Wallet size={14} />} label="Avanslar" tid="tab-advances" />
      </div>

      {tab === "timesheet"
        ? <TimesheetPanel employeeId={id} year={year} month={month} setYear={setYear} setMonth={setMonth} />
        : <AdvancesPanel employeeId={id} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
            className={`px-4 py-2 text-sm border-b-2 flex items-center gap-1.5 -mb-px
                       ${active ? "border-black font-semibold" : "border-transparent text-neutral-500 hover:text-black"}`}>
      {icon}{label}
    </button>
  );
}

function TimesheetPanel({ employeeId, year, month, setYear, setMonth }) {
  const [entries, setEntries] = useState({});
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/timesheet?year=${year}&month=${month}`);
      const map = {};
      (data.entries || []).forEach((e) => { map[e.date] = e; });
      setEntries(map);
      setTotals(data.totals);
    } finally { setLoading(false); }
  }, [employeeId, year, month]);

  useEffect(() => { load(); }, [load]);

  const dCount = daysInMonth(year, month);
  const days = useMemo(() => Array.from({ length: dCount }, (_, i) => {
    const d = new Date(year, month - 1, i + 1);
    const iso = d.toISOString().slice(0, 10);
    const dow = d.getDay();  // 0=Sun ... 6=Sat
    return { iso, dow, dayNum: i + 1 };
  }), [year, month, dCount]);

  const patch = (iso, field, value) => {
    setEntries((prev) => ({ ...prev, [iso]: { ...(prev[iso] || { date: iso }), [field]: value } }));
  };

  const persist = async (iso) => {
    const entry = entries[iso] || { date: iso };
    try {
      await api.post(`/employees/${employeeId}/timesheet`, {
        date: iso,
        status: entry.status || null,
        check_in: entry.check_in || null,
        check_out: entry.check_out || null,
        overtime_hours: entry.overtime_hours === "" || entry.overtime_hours == null ? null : Number(entry.overtime_hours),
        notes: entry.notes || null,
      });
      // Otomatik mesai hesaplama önerisi — sadece mesai boşsa
      if ((entry.check_in && entry.check_out) && (!entry.overtime_hours || entry.overtime_hours === 0)) {
        const ot = calcOvertime(entry.check_in, entry.check_out);
        if (ot !== null && ot > 0) {
          patch(iso, "overtime_hours", ot);
          await api.post(`/employees/${employeeId}/timesheet`, {
            date: iso,
            status: entry.status || null,
            check_in: entry.check_in, check_out: entry.check_out,
            overtime_hours: ot,
            notes: entry.notes || null,
          });
        }
      }
      load();  // totals + sync
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 brutal-card p-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} data-testid="ts-prev-month" className="p-1.5 hover:bg-neutral-100 border border-black/10"><ChevronLeft size={14} /></button>
          <div className="font-display font-semibold min-w-[160px] text-center" data-testid="ts-period">
            {TR_MONTHS[month - 1]} {year}
          </div>
          <button onClick={nextMonth} data-testid="ts-next-month" className="p-1.5 hover:bg-neutral-100 border border-black/10"><ChevronRight size={14} /></button>
        </div>
        {totals && (
          <div className="text-xs text-neutral-600 flex flex-wrap items-center gap-x-4 gap-y-1">
            {Object.entries(totals.by_status || {}).map(([k, v]) => {
              const opt = STATUS_OPTS.find((s) => s.value === k);
              return (
                <span key={k} className="flex items-center gap-1">
                  <span className={`inline-block w-2 h-2 border ${opt?.color || ""}`}></span>
                  {opt?.label}: <b>{v}</b>
                </span>
              );
            })}
            <span className="border-l border-neutral-300 pl-3">
              Toplam Mesai: <b className="font-mono-num text-black">{totals.overtime_hours} saat</b>
            </span>
          </div>
        )}
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="timesheet-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-3 py-2.5 label-caps w-24">Tarih</th>
              <th className="text-left px-3 py-2.5 label-caps">Durum</th>
              <th className="text-left px-3 py-2.5 label-caps w-24">Giriş</th>
              <th className="text-left px-3 py-2.5 label-caps w-24">Çıkış</th>
              <th className="text-right px-3 py-2.5 label-caps w-24">Mesai (sa)</th>
              <th className="text-left px-3 py-2.5 label-caps">Not</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-6 text-neutral-500">Yükleniyor…</td></tr>
            ) : days.map(({ iso, dow, dayNum }) => {
              const e = entries[iso] || {};
              const isWeekend = dow === 0 || dow === 6;
              const dowLabel = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"][dow];
              const statusOpt = STATUS_OPTS.find((s) => s.value === e.status);
              return (
                <tr key={iso} className={`border-b border-black/5 ${isWeekend ? "bg-neutral-50/60" : ""}`}
                    data-testid={`ts-row-${iso}`}>
                  <td className="px-3 py-1.5 font-mono-num text-xs">
                    <b>{String(dayNum).padStart(2, "0")}</b>
                    <span className={`ml-1 text-[10px] ${isWeekend ? "text-rose-500" : "text-neutral-400"}`}>{dowLabel}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={e.status || ""}
                            data-testid={`ts-status-${iso}`}
                            onChange={(ev) => { patch(iso, "status", ev.target.value || null); }}
                            onBlur={() => persist(iso)}
                            className={`text-xs border px-1.5 py-1 outline-none ${statusOpt?.color || "border-black/15 bg-white"}`}>
                      {STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="time" value={e.check_in || ""}
                           data-testid={`ts-in-${iso}`}
                           onChange={(ev) => patch(iso, "check_in", ev.target.value)}
                           onBlur={() => persist(iso)}
                           className="text-xs border border-black/15 px-1.5 py-1 font-mono-num w-full" />
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="time" value={e.check_out || ""}
                           data-testid={`ts-out-${iso}`}
                           onChange={(ev) => patch(iso, "check_out", ev.target.value)}
                           onBlur={() => persist(iso)}
                           className="text-xs border border-black/15 px-1.5 py-1 font-mono-num w-full" />
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <input type="number" step="0.25" min="0" value={e.overtime_hours ?? ""}
                           data-testid={`ts-ot-${iso}`}
                           onChange={(ev) => patch(iso, "overtime_hours", ev.target.value)}
                           onBlur={() => persist(iso)}
                           className="text-xs border border-black/15 px-1.5 py-1 font-mono-num text-right w-full" />
                  </td>
                  <td className="px-3 py-1.5">
                    <input type="text" value={e.notes || ""}
                           data-testid={`ts-notes-${iso}`}
                           onChange={(ev) => patch(iso, "notes", ev.target.value)}
                           onBlur={() => persist(iso)}
                           placeholder="—"
                           className="text-xs border border-black/15 px-1.5 py-1 w-full" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvancesPanel({ employeeId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), amount: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/employees/${employeeId}/advances`);
    setItems(data.items || []); setTotal(data.total || 0);
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Tutar girin"); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/advances`, {
        date: form.date, amount: Number(form.amount),
        description: form.description || null,
      });
      toast.success("Avans eklendi");
      setForm({ date: todayISO(), amount: "", description: "" });
      setShowForm(false); load();
    } catch (e) {
      toast.error("Eklenemedi: " + (e?.response?.data?.detail || e?.message || ""));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu avans silinsin mi?")) return;
    try {
      await api.delete(`/advances/${id}`);
      toast.success("Silindi"); load();
    } catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 brutal-card p-4">
        <div>
          <div className="label-caps">Toplam Verilen Avans</div>
          <div className="font-mono-num text-2xl mt-1 text-rose-600" data-testid="advances-total">{fmtTRY(total)}</div>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="advance-add"
                className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2">
          <Plus size={14} /> Yeni Avans
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} data-testid="advance-form"
              className="brutal-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="text-xs">
            <div className="label-caps mb-1">Tarih</div>
            <input type="date" required data-testid="advance-date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="border border-black/20 px-3 py-2 font-mono-num text-sm w-full" />
          </label>
          <label className="text-xs">
            <div className="label-caps mb-1">Tutar (₺)</div>
            <input type="number" step="0.01" required data-testid="advance-amount" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   className="border border-black/20 px-3 py-2 font-mono-num text-sm w-full" />
          </label>
          <label className="text-xs md:col-span-2">
            <div className="label-caps mb-1">Açıklama</div>
            <input type="text" data-testid="advance-desc" value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="Örn: Aylık avans" className="border border-black/20 px-3 py-2 text-sm w-full" />
          </label>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)}
                    className="border border-black/20 px-3 py-1.5 text-xs">Vazgeç</button>
            <button type="submit" disabled={saving} data-testid="advance-save"
                    className="brutal-btn bg-black text-white px-3 py-1.5 text-xs disabled:opacity-40">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      )}

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="advances-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps w-32">Tarih</th>
              <th className="text-right px-4 py-3 label-caps w-40">Tutar</th>
              <th className="text-left px-4 py-3 label-caps">Açıklama</th>
              <th className="px-4 py-3 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-neutral-500 text-sm">Kayıt yok.</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="border-b border-black/5 hover:bg-neutral-50">
                <td className="px-4 py-2.5 font-mono-num">{fmtDate(a.date)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-rose-600 font-semibold">{fmtTRY(a.amount)}</td>
                <td className="px-4 py-2.5 text-neutral-700">{a.description || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => remove(a.id)} data-testid={`advance-delete-${a.id}`}
                          className="p-1 hover:bg-rose-50"><Trash2 size={13} className="text-rose-600" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
